import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

// Sana tanlash — admin panelning DateTimePicker'iga o'xshash chiroyli kalendar,
// faqat sana (vaqtsiz). Qiymat/onChange "YYYY-MM-DD" (ISO) formatda — backend
// query paramlariga to'g'ridan-to'g'ri boradi; ko'rsatilishi esa DD.MM.YYYY.
const LOCALE_MAP = { uz: 'uz-UZ', 'uz-cyrl': 'uz-Cyrl-UZ', ru: 'ru-RU', en: 'en-US' };
const pad = n => String(n).padStart(2, '0');
const startOfDay = dt => { const x = new Date(dt); x.setHours(0, 0, 0, 0); return x; };
const toIso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const parseIso = (v) => {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  return { y: +y, m: +mo - 1, d: +d };
};

export default function DatePicker({ value, onChange, placeholder = '', className = '', disableFuture = false, minDate = null }) {
  const lang = useSelector((s) => s.lang.current);
  const locale = LOCALE_MAP[lang] || 'uz-UZ';
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const now = new Date();
  const parsed = parseIso(value);
  const sel = parsed || { y: now.getFullYear(), m: now.getMonth(), d: now.getDate() };
  const [view, setView] = useState({ y: sel.y, m: sel.m });

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < 340 && r.top > spaceBelow;
    setPos({
      left: Math.min(r.left, window.innerWidth - 280),
      top: openUp ? undefined : r.bottom + 4,
      bottom: openUp ? window.innerHeight - r.top + 4 : undefined,
    });
  };

  useEffect(() => {
    if (!open) return;
    if (parsed) setView({ y: parsed.y, m: parsed.m });
    place();
    const onMove = () => place();
    const onClick = (e) => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    document.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      document.removeEventListener('mousedown', onClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickDay = (d) => { onChange(toIso(view.y, view.m, d)); setOpen(false); };

  const first = new Date(view.y, view.m, 1);
  const lead = (first.getDay() + 6) % 7; // Dushanbadan boshlab
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const todayStart = startOfDay(now);
  const weekdays = [...Array(7)].map((_, i) => new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: 'short' }));
  const monthLabel = new Date(view.y, view.m, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const prevMonth = () => setView(v => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 });
  const nextMonth = () => setView(v => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 });

  const displayText = parsed
    ? new Date(parsed.y, parsed.m, parsed.d).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';

  return (
    <>
      <button ref={btnRef} type="button" onClick={() => setOpen(o => !o)}
        className={`input flex items-center gap-2 text-left ${className}`}>
        <Calendar className="w-4 h-4 text-ink-tertiary shrink-0" />
        <span className={parsed ? 'text-ink' : 'text-ink-disabled'}>{displayText || placeholder}</span>
      </button>

      {open && pos && createPortal(
        <div ref={panelRef}
          style={{ position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom, width: 280, zIndex: 80 }}
          className="bg-white border border-surface-200 rounded-xl shadow-modal p-3">
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-semibold text-ink capitalize">{monthLabel}</span>
            <button type="button" onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><ChevronRight className="w-4 h-4" /></button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {weekdays.map((w, i) => <div key={i} className="text-center text-[10px] font-medium text-ink-tertiary uppercase py-1">{w}</div>)}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {Array.from({ length: lead }).map((_, i) => <div key={`b${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => {
              const cellDate = new Date(view.y, view.m, d);
              const cellStart = startOfDay(cellDate);
              const minStart = minDate ? startOfDay(new Date(minDate)) : null;
              const blocked = (disableFuture && cellStart > todayStart) || (minStart && cellStart < minStart);
              const isSel = parsed && parsed.y === view.y && parsed.m === view.m && parsed.d === d;
              const isToday = cellStart.getTime() === todayStart.getTime();
              return (
                <button key={d} type="button" disabled={blocked} onClick={() => pickDay(d)}
                  className={`h-8 rounded-lg text-sm font-medium transition-colors
                    ${isSel ? 'bg-primary-600 text-white' : blocked ? 'text-ink-disabled cursor-not-allowed' : 'text-ink hover:bg-surface-100'}
                    ${!isSel && isToday ? 'ring-1 ring-primary-300' : ''}`}>
                  {d}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
